/**
 * ForgotPasswordPage — request a password reset email.
 *
 * Public route. Calls POST /v1/auth/forgot-password.
 * Always shows the confirmation message after submit (anti-enumeration:
 * the API returns 200 whether the email exists or not).
 */
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Divider,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { normalizedEmailSchema } from '@crm98/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiClient } from '@/lib/api-client.js';
import { filledInputSx, outerBoxSx, BrandMark } from './auth-shared.js';

const schema = z.object({
  email: normalizedEmailSchema,
});

type FormValues = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (data) => {
    setSubmitting(true);
    try {
      await apiClient.post('/v1/auth/forgot-password', { body: { email: data.email } });
    } finally {
      // Always show confirmation regardless of API outcome (anti-enumeration)
      setSubmitted(true);
      setSubmitting(false);
    }
  });

  return (
    <Box sx={outerBoxSx}>
      <BrandMark />

      {/* Heading */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
        Reset Your Password
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 440 }}>
        {submitted ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              If an account exists with that email, a password reset link has been sent. Check your
              inbox.
            </Alert>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" variant="body2">
                Back to log in
              </Link>
            </Box>
          </>
        ) : (
          <form onSubmit={onSubmit}>
            <TextField
              hiddenLabel
              placeholder="Email"
              type="email"
              autoComplete="email"
              variant="filled"
              {...register('email')}
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              disabled={submitting}
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                },
              }}
              sx={filledInputSx}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              loading={submitting}
              sx={{ mt: 2, borderRadius: 8, py: 1.5, fontWeight: 700, fontSize: '1rem' }}
            >
              Send Reset Link
            </Button>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" variant="body2">
                Back to log in
              </Link>
            </Box>
          </form>
        )}
      </Box>
    </Box>
  );
}
